import React from 'react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { colors } from '@kangaroo/ui';
import { PAGE_ICONS } from '../../lib/pageIcons';
import { SettingsContent, type SettingsSection } from './SettingsPage';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  section: SettingsSection;
  onSectionChange: (section: SettingsSection) => void;
}

export function SettingsDrawer({ open, onClose, section, onSectionChange }: SettingsDrawerProps) {
  if (!open) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560, lg: 640 },
          maxWidth: '100vw',
          bgcolor: 'background.default',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box
          sx={{
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: { xs: 1.1, sm: 1.4 },
            py: 1.05,
            bgcolor: 'background.default',
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1.2, minHeight: 40 }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.9} sx={{ alignItems: 'center', mb: 0.25 }}>
                <Box sx={{ display: 'flex', color: colors.primary[300], '& .MuiSvgIcon-root': { fontSize: 20 } }}>
                  {PAGE_ICONS.settings}
                </Box>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, lineHeight: 1.2 }}>Settings</Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                Interface, profile, units, permissions, and security
              </Typography>
            </Box>
            <IconButton
              aria-label="Close settings"
              size="small"
              onClick={onClose}
              sx={{ width: 32, height: 32, color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 0.8 }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 1.1, sm: 1.4 }, py: 1.25 }}>
          <SettingsContent section={section} onSectionChange={onSectionChange} embedded />
        </Box>
      </Box>
    </Drawer>
  );
}
